import { Injectable } from '@angular/core';
import { UserService } from './user.service';
import { AuthenticationService } from './auth.service';
import { DesignService } from './design.service';
import { Router } from '@angular/router';

@Injectable()
export class AppLoadService {

  constructor(
    private userService: UserService,
    private authService: AuthenticationService,
    private designService: DesignService,
    private router: Router) { }

  loadSettings(): Promise<any> {
    const promise = this.attemptAuthenticate()
      .then(() => this.loadAuthenticatedUser())
      .then(() => this.loadDesignPolicy())
      .then(() => this.loadTenantSettings())
      .then(() => this.interceptAuthCode());

    return promise
  }

  private attemptAuthenticate(): Promise<any> {
    let queryString = location.href.split("?").pop()
    let urlParams = new URLSearchParams(queryString)
    let clientCredentials = urlParams.get("client_credentials")
    let accessToken = urlParams.get("access_token")

    if(clientCredentials) {
      this.replaceUrl("client_credentials", urlParams);

      return this.authenticateByClientCredentials(clientCredentials)
    } else if(accessToken) {
      this.replaceUrl("access_token", urlParams)

      return this.authenticateByAccessToken(accessToken)
    }

    return Promise.resolve()
  }

  private authenticateByClientCredentials(credentials: string) {
    if(this.isBase64(credentials)) {
      credentials = atob(credentials)
    }

    let credencials = JSON.parse(credentials)
    if(credencials && credencials.client && credencials.secret) {
      return this.authService.loginAssociate(
          credencials.client, 
          credencials.secret, 
          credencials.associateLogin)
        .toPromise()
    }
  }

  private authenticateByAccessToken(accessToken: string) {
    this.authService.setAuthToken(accessToken)

    return this.authService
      .validateAccess()
      .toPromise()
      .catch(() => {
        this.authService.logout()
        this.router.navigate(['/'])
      })
  }

  private replaceUrl(paramName: string, urlParams: URLSearchParams) {
    let newParams = "";
    urlParams.forEach((value, key) => {
      if (key != paramName) {
        newParams += (newParams) ? "&" : "?";
        newParams += `${key}=${value}`;
      }
    });

    window.history.replaceState({}, "",
      `${location.origin}${location.pathname}${newParams}`);
  }

  private isBase64(str) {
    if (str ==='' || str.trim() ===''){ return false; }
    try {
        return btoa(atob(str)) == str;
    } catch (err) {
        return false;
    }
  }

  private loadAuthenticatedUser(): Promise<any> {
    return this.userService
      .loadUserAttributes()
      .toPromise()
      .catch(() => {
        this.authService.logout()
        this.router.navigate(['/'])
      })
  }

  private loadDesignPolicy(): Promise<any> {
    if (!this.authService.isAuthenticated()) {
      const hostname = window.location.hostname;
      if (this.isCustomDomain(hostname)) {
        return this.designService.loadDesignPolicyByDomain(hostname).toPromise()
          .catch((error) => {
            localStorage.removeItem("designPolicyId")
            localStorage.removeItem("designAttributes")
            this.designService.setColors()
            console.log("design attributes not loaded")
          });
      } else {
        this.designService.setColors();
        return Promise.resolve();
      }
    }

    return this.designService
      .loadDesignPolicy()
      .toPromise()
      .catch((error) => {
        localStorage.removeItem("designPolicyId")
        localStorage.removeItem("designAttributes")
        this.designService.setColors()
        console.log("design attributes not loaded")
      })
  }

  private isCustomDomain(hostname: string): boolean {
    return !hostname.includes(".buildflex.io") && hostname !== 'localhost';
  }

  private loadTenantSettings(): Promise<any> {
    if(!this.authService.isAuthenticated()) {
      let user =  this.authService.getUser()

      if(user) {
        localStorage.setItem("tid", user.tenantId)
        localStorage.setItem("wid", user.workspaceId)
      }
    }

    return Promise.resolve()
  }

  private interceptAuthCode(): void {
    const currentUrl = window.location.href;

    if (currentUrl.includes('auzure-auth-callback#') && currentUrl.includes('code=')) {
      const baseUrl = `${location.origin}/azure-auth-callback${location.hash}`;
      
      window.history.replaceState(null, '', baseUrl);
    }
  }
}